import { create } from 'zustand';
// Import Preset and Client types
import { Preset, Client } from '@/store/types'; // Added Client
import type { Feature, Point, Polygon } from 'geojson';
import type { CountryInfo } from '@/utils/countryDetection';
import type { IsochroneResult } from '@/utils/isochroneAnalysis';
import type { DateRange } from 'react-day-picker';

// Define visualization types - Keep this if used elsewhere, or move to types.ts if appropriate
export type VisualizationType = 'dots' | 'heatmap' | 'clusters' | 'graduated-circles' | '3d-extrusion';

// Restore type definitions previously removed
export type LogicOperator = 'AND' | 'OR';
export type CRMEventType = string | null;
export interface FilterOption {
    name: string;
    count: number;
    // Optional GeoJSON [lng, lat] — populated by useSchools for schools-collection
    // entries so "Go to school" and "Run radius analysis" can resolve coords
    // without an extra fetch.
    coordinates?: [number, number];
}

export type SpatialFilterType = 'radius' | 'region' | 'postcode' | 'bounds';

export interface SpatialFilterState {
  type: SpatialFilterType;
  label: string;
  center?: [number, number];
  value?: string;
  radius?: number;
  units?: 'km' | 'miles';
  bounds?: [[number, number], [number, number]];
}

// Define CRM feature type for store
export interface CRMProperties {
  [key: string]: any;
  "Contact ID": string;
  "Postcode"?: string;
  "Enquiry Subject"?: string;
  "School on Enquiry"?: string;
  "Course Title"?: string;
  "Offer"?: string;
  "Enrolled"?: string;
}

export type CRMFeature = Feature<Point, CRMProperties>;

// Segment selector for UG / PGT / PGR data slices. 'all' represents the
// no-filter state (used by the segment toggle and `useCRMData` filtering).
export type Segment = 'all' | 'undergraduate' | 'postgraduate_taught' | 'postgraduate_research';
// No FilterPreset definition needed here

// Comparison mode selector for the sidebar "Compare to" dropdown.
// 'none' disables comparison; 'previous-year' shifts the primary date range
// backwards by exactly one year; 'previous-period' shifts backwards by the
// duration of the primary range (to - from).
export type ComparisonMode = 'none' | 'previous-year' | 'previous-period';

// Derive the comparison date range from the primary range and the selected mode.
// Returns null when mode is 'none' or when the primary range cannot be shifted
// (e.g., 'previous-period' but only one bound is set).
export function deriveComparisonRange(
  primary: { from?: Date; to?: Date } | null,
  mode: ComparisonMode
): { from?: Date; to?: Date } | null {
  if (mode === 'none' || !primary) return null;
  const { from, to } = primary;
  if (mode === 'previous-year') {
    const shift = (d?: Date) => {
      if (!d) return undefined;
      const copy = new Date(d.getTime());
      copy.setFullYear(copy.getFullYear() - 1);
      return copy;
    };
    const shifted = { from: shift(from), to: shift(to) };
    if (!shifted.from && !shifted.to) return null;
    return shifted;
  }
  if (mode === 'previous-period') {
    if (!from || !to) return null;
    const duration = to.getTime() - from.getTime();
    if (!Number.isFinite(duration) || duration <= 0) return null;
    return {
      from: new Date(from.getTime() - duration),
      to: new Date(to.getTime() - duration),
    };
  }
  return null;
}

// Comparison Mode State
export interface ComparisonState {
  isComparisonMode: boolean;
  baselineFilters: {
    selectedSchool: string;
    selectedEventType: CRMEventType;
    selectedApplicantType: string;
    selectedSubject: string;
    selectedPostcodePrefix: string;
    timeRange?: string;
  };
  comparisonFilters: {
    selectedSchool: string;
    selectedEventType: CRMEventType;
    selectedApplicantType: string;
    selectedSubject: string;
    selectedPostcodePrefix: string;
    timeRange?: string;
  };
  baselineData: {
    enquiryCount: number;
    eventCount: number;
    applicantCount: number;
    applicationCount: number;
    attendeeCount: number;
    feederSchoolCount: number;
    impressionsCount: number;
    clicksCount: number;
    sessionsCount: number;
    eighteenYearOldsCount: number;
    enrolmentCount: number;
  };
  comparisonData: {
    enquiryCount: number;
    eventCount: number;
    applicantCount: number;
    applicationCount: number;
    attendeeCount: number;
    feederSchoolCount: number;
    impressionsCount: number;
    clicksCount: number;
    sessionsCount: number;
    eighteenYearOldsCount: number;
    enrolmentCount: number;
  };
}

// Define UserPreferences interface for role-based defaults
export interface UserPreferences {
  defaultView: 'map' | 'global';
  defaultMetrics: string[];
  defaultFilters: Partial<{
    selectedSchool: string;
    selectedEventType: CRMEventType;
    selectedApplicantType: string;
    selectedSubject: string;
    selectedPostcodePrefix: string;
    timeRange?: string;
  }>;
  dashboardLayout: 'compact' | 'detailed';
}

// Define the store state
export interface StoreState { // Export the interface
  // Client selection
  currentClient: Client | null; // Changed to Client object or null
  availableClients: Client[]; // Added list of available clients
  setCurrentClient: (client: Client | null) => void; // Changed signature
  setAvailableClients: (clients: Client[]) => void; // Added setter
  campaignDefinitions: Array<{ term: string; label: string }>;
  setCampaignDefinitions: (definitions: Array<{ term: string; label: string }>) => void;

  // === Layer Visibility, Viz, Color ===
  showEnquiries: boolean;
  setShowEnquiries: (show: boolean) => void;
  enquiriesVizType: VisualizationType;
  setEnquiriesVizType: (type: VisualizationType) => void;
  enquiriesColor: string;
  setEnquiriesColor: (color: string) => void;
  enquiriesOpacity: number;
  setEnquiriesOpacity: (opacity: number) => void;

  showEvents: boolean;
  setShowEvents: (show: boolean) => void;
  eventsVizType: VisualizationType;
  setEventsVizType: (type: VisualizationType) => void;
  eventsColor: string;
  setEventsColor: (color: string) => void;
  eventsOpacity: number;
  setEventsOpacity: (opacity: number) => void;

  showApplicant: boolean;
  setShowApplicant: (show: boolean) => void;
  applicantVizType: VisualizationType;
  setApplicantVizType: (type: VisualizationType) => void;
  applicantColor: string;
  setApplicantColor: (color: string) => void;
  applicantOpacity: number;
  setApplicantOpacity: (opacity: number) => void;

  // Add new states for MetricSummaryBar
  showApplications: boolean;
  setShowApplications: (show: boolean) => void;
  showAttendees: boolean;
  setShowAttendees: (show: boolean) => void;
  showFeederSchools: boolean;
  setShowFeederSchools: (show: boolean) => void;
  showUniversities: boolean;
  setShowUniversities: (show: boolean) => void;
  // showEnquiries already exists

  // US Funnel stages (Admit, Deposit, Enroll)
  showOffers: boolean;  // Admit stage
  setShowOffers: (show: boolean) => void;
  showDeposits: boolean;  // Deposit stage
  setShowDeposits: (show: boolean) => void;
  showEnrollments: boolean;  // Enroll stage
  setShowEnrollments: (show: boolean) => void;

  // === Filter Selections ===
  selectedDateRange: DateRange | null;
  setSelectedDateRange: (range: DateRange | null) => void;
  selectedTimeRange: string | null;
  setSelectedTimeRange: (label: string | null) => void;
  selectedSegment: 'all' | 'undergraduate' | 'postgraduate_taught' | 'postgraduate_research';
  setSelectedSegment: (segment: 'all' | 'undergraduate' | 'postgraduate_taught' | 'postgraduate_research') => void;
  selectedSchool: string;
  setSelectedSchool: (school: string) => void;
  selectedEventType: CRMEventType;
  setSelectedEventType: (eventType: CRMEventType) => void;
  selectedApplicantType: string;
  setSelectedApplicantType: (applicantType: string) => void;
  selectedApplicantTypes: string[];
  setSelectedApplicantTypes: (applicantTypes: string[]) => void;
  selectedSubject: string;
  setSelectedSubject: (subject: string) => void;
  selectedPostcodePrefix: string;
  setSelectedPostcodePrefix: (prefix: string) => void;
  selectedUniversity: string;
  setSelectedUniversity: (university: string) => void;
  selectedYears: string[]; // Added for year cohort filtering
  setSelectedYears: (years: string[]) => void;

  // === Bottom Panel Dropdown Selections ===
  awarenessSelection: string;
  setAwarenessSelection: (selection: string) => void;
  considerationSelection: string;
  setConsiderationSelection: (selection: string) => void;
  conversionSelection: string;
  setConversionSelection: (selection: string) => void;

  // === Multi-Selections ===
  selectedSchools: string[];
  setSelectedSchools: (schools: string[]) => void;
  selectedSubjects: string[];
  setSelectedSubjects: (subjects: string[]) => void;
  selectedPostcodePrefixes: string[];
  setSelectedPostcodePrefixes: (postcodes: string[]) => void;
  selectedEventTypes: string[];
  setSelectedEventTypes: (eventTypes: string[]) => void;
  selectedCounties: string[];
  setSelectedCounties: (counties: string[]) => void;
  selectedUniversities: string[];
  setSelectedUniversities: (universities: string[]) => void;
  topBarSearchTerm: string;
  setTopBarSearchTerm: (term: string) => void;

  // === Logic Operators ===
  logicEnquiryEvent: LogicOperator;
  setLogicEnquiryEvent: (logic: LogicOperator) => void;
  logicEventApplicant: LogicOperator;
  setLogicEventApplicant: (logic: LogicOperator) => void;

  // === Available Filter Options (Populated from data) ===
  availableSchools: FilterOption[]; // Use FilterOption type
  setAvailableSchools: (schools: FilterOption[]) => void;
  availableEventTypes: FilterOption[]; // Use FilterOption type
  setAvailableEventTypes: (types: FilterOption[]) => void;
  availableApplicantTypes: FilterOption[]; // Use FilterOption type
  setAvailableApplicantTypes: (types: FilterOption[]) => void;
  availableSubjects: FilterOption[]; // Use FilterOption type
  setAvailableSubjects: (subjects: FilterOption[]) => void;
  availablePostcodePrefixes: FilterOption[]; // Use FilterOption type
  setAvailablePostcodePrefixes: (prefixes: FilterOption[]) => void;
  availableYears: string[]; // Added for available years from data
  setAvailableYears: (years: string[]) => void;
  availableCounties: FilterOption[];
  setAvailableCounties: (counties: FilterOption[]) => void;
  availableUniversities: FilterOption[];
  setAvailableUniversities: (universities: FilterOption[]) => void;

  // === Dynamic Counts ===
  enquiryCount: number;
  setEnquiryCount: (count: number) => void;
  eventCount: number;
  setEventCount: (count: number) => void;
  applicantCount: number;
  setApplicantCount: (count: number) => void;
  enrolmentCount: number;
  setEnrolmentCount: (count: number) => void;
  // Add counts for MetricSummaryBar
  applicationCount: number;
  setApplicationCount: (count: number) => void;
  offersCount: number;
  setOffersCount: (count: number) => void;
  depositsCount: number;
  setDepositsCount: (count: number) => void;
  attendeeCount: number;
  setAttendeeCount: (count: number) => void;
  feederSchoolCount: number;
  setFeederSchoolCount: (count: number) => void;
  impressionsCount: number; // Added
  clicksCount: number; // Added
  setImpressionsCount: (count: number) => void; // Added
  setClicksCount: (count: number) => void; // Added
  universitiesCount: number; // Universities count
  setUniversitiesCount: (count: number) => void; // Universities count setter
  sessionsCount: number; // GA4 sessions
  setSessionsCount: (count: number) => void;
  showSessions: boolean;
  setShowSessions: (show: boolean) => void;

  // Market share demographics
  eighteenYearOldsCount: number;
  setEighteenYearOldsCount: (count: number) => void;
  showEighteenYearOlds: boolean;
  setShowEighteenYearOlds: (show: boolean) => void;


  // === Base Layer Toggles (Task 5) ===
  showUkCounties: boolean;
  setShowUkCounties: (show: boolean) => void;
  showUkOoh: boolean;
  setShowUkOoh: (show: boolean) => void;
  // Add state for client-specific school layers
  showTargetSchools: boolean;
  setShowTargetSchools: (show: boolean) => void;


  showOutreachSchools: boolean;
  setShowOutreachSchools: (show: boolean) => void;
  showUkPcons: boolean; // Added for UK PCONs - Will be removed/replaced
  setShowUkPcons: (show: boolean) => void; // Added for UK PCONs - Will be removed/replaced
  // Add toggles for other base layers if needed (target regions, schools etc.) - Replaced below
  baseLayerVisibility: Record<string, boolean>; // Stores visibility state for dynamic base layers (key: layer id)
  setBaseLayerVisibility: (layerId: string, isVisible: boolean) => void; // Action to toggle visibility

  // Clear data trigger - increments when Clear All is clicked
  clearDataTrigger: number;
  triggerClearData: () => void;

  // === Analytics Layer Toggles & Config ===
  showImpressions: boolean;
  setShowImpressions: (show: boolean) => void;
  impressionsVizType: VisualizationType;
  setImpressionsVizType: (type: VisualizationType) => void;
  impressionsColor: string;
  setImpressionsColor: (color: string) => void;
  impressionsOpacity: number;
  setImpressionsOpacity: (opacity: number) => void;

  showClicks: boolean;
  setShowClicks: (show: boolean) => void;
  clicksVizType: VisualizationType;
  setClicksVizType: (type: VisualizationType) => void;
  clicksColor: string;
  setClicksColor: (color: string) => void;
  clicksOpacity: number;
  setClicksOpacity: (opacity: number) => void;

  showConversions: boolean;
  setShowConversions: (show: boolean) => void;
  
  // After showConversions and setShowConversions, add:
  conversionsVizType: VisualizationType;
  setConversionsVizType: (type: VisualizationType) => void;
  conversionsColor: string;
  setConversionsColor: (color: string) => void;
  conversionsOpacity: number;
  setConversionsOpacity: (opacity: number) => void;
  conversionsCount: number;
  setConversionsCount: (count: number) => void;

  // Preset refresh trigger
  sidebarPresetsRefreshCounter: number;
  triggerSidebarPresetsRefresh: () => void;

  // UI State Management for dropdowns coordination
  activeDropdown: string | null; // Track which dropdown is currently open
  setActiveDropdown: (dropdownId: string | null) => void;

  // Presets state and actions - Use Preset type and Record structure
  presets: Record<string, Preset>; // Store presets as an object/record
  addPreset: (preset: Preset) => void; // Action takes a Preset
  deletePreset: (presetId: string) => void; // Delete by ID (assuming Preset has id or preset_id)
  loadPresets: (presets: Record<string, Preset>) => void; // Load presets as a Record

  // Applied Preset Tracking
  appliedPresetId: string | null;
  applyPreset: (presetId: string) => void;

  // Map bounds for spatial filtering
  currentMapBounds: [[number, number], [number, number]] | null;
  setCurrentMapBounds: (bounds: [[number, number], [number, number]] | null) => void;
  mapViewFilterEnabled: boolean;
  setMapViewFilterEnabled: (enabled: boolean) => void;
  spatialFilter: SpatialFilterState | null;
  setSpatialFilter: (filter: SpatialFilterState | null) => void;

  // Filtered data for postcode analysis
  filteredCRMFeatures: CRMFeature[];
  setFilteredCRMFeatures: (features: CRMFeature[]) => void;
  allCRMFeatures: CRMFeature[];
  setAllCRMFeatures: (features: CRMFeature[]) => void;
  yearFilteredCRMFeatures: CRMFeature[]; // All features filtered by year only
  setYearFilteredCRMFeatures: (features: CRMFeature[]) => void;

  // GA4 Analytics Data
  ga4Overview: {
    sessions: number;
    users: number;
    page_views: number;
    new_users: number;
    bounce_rate: number;
    avg_session_duration: number;
    engagement_rate: number;
  } | null;
  setGA4Overview: (overview: StoreState['ga4Overview']) => void;
  ga4TrafficSources: Array<{
    name: string;
    sessions: number;
    users: number;
    percentage: number;
  }>;
  setGA4TrafficSources: (sources: StoreState['ga4TrafficSources']) => void;
  ga4Locations: {
    cities: Array<{ name: string; sessions: number; users: number }>;
    countries: Array<{ name: string; sessions: number; users: number }>;
  } | null;
  setGA4Locations: (locations: StoreState['ga4Locations']) => void;
  ga4ContentEngagement: Array<{
    page_path: string;
    page_title: string;
    views: number;
    avg_engagement_time: number;
  }>;
  setGA4ContentEngagement: (content: StoreState['ga4ContentEngagement']) => void;
  ga4Events: Array<{
    name: string;
    count: number;
  }>;
  setGA4Events: (events: StoreState['ga4Events']) => void;

  // Country selection and highlighting
  selectedCountry: string | null;
  setSelectedCountry: (country: string | null) => void;
  countryHighlightVisible: boolean;
  setCountryHighlightVisible: (visible: boolean) => void;
  
  // Country information for clicked countries
  selectedCountryInfo: CountryInfo | null;
  setSelectedCountryInfo: (info: CountryInfo | null) => void;
  
  // User preferences
  userPreferences: UserPreferences | null;
  setUserPreferences: (preferences: UserPreferences) => void;
  applyUserPreferences: (preferences: UserPreferences) => void;
  
  // Comparison mode state and actions
  comparisonState: ComparisonState;
  setComparisonMode: (enabled: boolean) => void;
  // Sidebar "Compare to" selector (separate from the boolean isComparisonMode flag).
  // Drives the date-range shift used to fetch comparison data in parallel.
  comparisonMode: ComparisonMode;
  setComparisonMode2: (mode: ComparisonMode) => void;
  // True while the parallel comparison-window CRM/marketing fetch is in flight.
  isComparisonLoading: boolean;
  setIsComparisonLoading: (loading: boolean) => void;
  setBaselineFilters: (filters: Partial<ComparisonState['baselineFilters']>) => void;
  setComparisonFilters: (filters: Partial<ComparisonState['comparisonFilters']>) => void;
  setBaselineData: (data: Partial<ComparisonState['baselineData']>) => void;
  setComparisonData: (data: Partial<ComparisonState['comparisonData']>) => void;
  captureCurrentAsBaseline: () => void;
  captureCurrentAsComparison: () => void;
  
  // Spatial Analysis - Isochrones
  activeIsochrones: IsochroneResult[];
  setActiveIsochrones: (isochrones: IsochroneResult[]) => void;
  addIsochrone: (isochrone: IsochroneResult) => void;
  clearIsochrones: () => void;
  isGeneratingIsochrone: boolean;
  setIsGeneratingIsochrone: (generating: boolean) => void;

  // Target schools list for reporting
  targetSchoolsList: string[];
  setTargetSchoolsList: (schools: string[]) => void;

  // Outreach schools list for reporting
  outreachSchoolsList: string[];
  setOutreachSchoolsList: (schools: string[]) => void;
}

// Type guard for VisualizationType
const isValidVisualizationType = (type: any): type is VisualizationType => {
  return ['dots', 'heatmap', 'clusters', 'graduated-circles', '3d-extrusion'].includes(type);
};

// Get role-based default preferences
export const getUserDefaults = (userRole: string, userEmail?: string): UserPreferences => {
  // Check if user has specific role based on email domain or other criteria
  const emailDomain = userEmail?.toLowerCase().split('@')[1] || '';
  
  // Check for specific university domains that might indicate recruitment managers
  const universityDomains = ['.uea.ac.uk', '.shu.ac.uk', '.kent.ac.uk', '.northampton.ac.uk', '.ac.uk'];
  const isUniversityUser = universityDomains.some(domain => emailDomain.endsWith(domain));
  
  // Determine role based on email patterns if userRole not explicitly set
  let effectiveRole = userRole;
  if (!effectiveRole && userEmail) {
    if (emailDomain.includes('marketing') || emailDomain.includes('digital')) {
      effectiveRole = 'marketing_analyst';
    } else if (emailDomain.includes('exec') || emailDomain.includes('director')) {
      effectiveRole = 'executive';
    } else if (isUniversityUser) {
      effectiveRole = 'recruitment_manager';
    }
  }
  
  switch (effectiveRole) {
    case 'recruitment_manager':
      return {
        defaultView: 'map',
        defaultMetrics: ['applications', 'attendees', 'feederSchools', 'enquiries'],
        defaultFilters: {
          timeRange: 'last3months'
        },
        dashboardLayout: 'detailed'
      };
    case 'marketing_analyst':
      return {
        defaultView: 'global',
        defaultMetrics: ['impressions', 'clicks', 'conversions', 'enquiries'],
        defaultFilters: {
          timeRange: 'lastMonth'
        },
        dashboardLayout: 'compact'
      };
    case 'executive':
      return {
        defaultView: 'global',
        defaultMetrics: ['applications', 'eighteenYearOlds', 'enquiries'],
        defaultFilters: {
          timeRange: 'lastYear'
        },
        dashboardLayout: 'compact'
      };
    default:
      return {
        defaultView: 'map',
        defaultMetrics: ['applications', 'enquiries'],
        defaultFilters: {},
        dashboardLayout: 'detailed'
      };
  }
};

// Create the store
export const useStore = create<StoreState>((set, get) => ({
  // Client selection
  currentClient: null, // Initialize as null
  availableClients: [] as any[],
  campaignDefinitions: [],
  setCurrentClient: (client) => {
    const definitions = (client?.campaigns ?? [])
      .filter((entry) => entry.term && entry.label)
      .map(({ term, label }) => ({ term, label }));

    set({
      currentClient: client,
      campaignDefinitions: definitions,
    });
    // Store the selected client ID in localStorage
    if (typeof window !== 'undefined') {
      if (client && client.id) {
        localStorage.setItem('last_selected_client_id', client.id);
        console.log(`Stored last selected client ID: ${client.id}`);
      } else {
        localStorage.removeItem('last_selected_client_id');
        console.log('Removed last selected client ID from storage.');
      }
    }
    // Trigger preset refresh when client changes
    get().triggerSidebarPresetsRefresh();
  },
  setAvailableClients: (clients) => {
    const normalizedClients = Array.isArray(clients) ? clients : [];
    set((state) => {
      const currentId = state.currentClient?.id;
      let nextCurrent = state.currentClient;
      let nextDefinitions = state.campaignDefinitions;

      if (currentId) {
        const refreshedClient = normalizedClients.find((client) => client.id === currentId);
        if (refreshedClient) {
          nextCurrent = refreshedClient;
          nextDefinitions = (refreshedClient.campaigns ?? [])
            .filter((entry) => entry.term && entry.label)
            .map(({ term, label }) => ({ term, label }));
        }
      }

      return {
        availableClients: normalizedClients,
        currentClient: nextCurrent,
        campaignDefinitions: nextDefinitions,
      };
    });
  },
  setCampaignDefinitions: (definitions) => {
    set({
      campaignDefinitions: Array.isArray(definitions)
        ? definitions
            .filter((entry) => entry.term && entry.label)
            .map(({ term, label }) => ({ term, label }))
        : [],
    });
  },

  // === Layer States ===
  showEnquiries: true,
  setShowEnquiries: (show) => set({ showEnquiries: show }),
  enquiriesVizType: 'clusters',
  setEnquiriesVizType: (type) => set({ enquiriesVizType: type }),
  enquiriesColor: '#06b6d4',  // Cyan - matches RFI Forms funnel slice
  setEnquiriesColor: (color) => set({ enquiriesColor: color }),
  enquiriesOpacity: 100,
  setEnquiriesOpacity: (opacity) => set({ enquiriesOpacity: opacity }),

  showEvents: false,
  setShowEvents: (show) => set({ showEvents: show }),
  eventsVizType: 'clusters',
  setEventsVizType: (type) => set({ eventsVizType: type }),
  eventsColor: '#059669',  // Emerald - matches Events funnel slice
  setEventsColor: (color) => set({ eventsColor: color }),
  eventsOpacity: 100,
  setEventsOpacity: (opacity) => set({ eventsOpacity: opacity }),

  showApplicant: false,
  setShowApplicant: (show) => set({ showApplicant: show }),
  applicantVizType: 'clusters',
  setApplicantVizType: (type) => set({ applicantVizType: type }),
  applicantColor: '#EF4444',  // Red - matches Apply funnel slice
  setApplicantColor: (color) => set({ applicantColor: color }),
  applicantOpacity: 100,
  setApplicantOpacity: (opacity) => set({ applicantOpacity: opacity }),

  // Add initial state and setters for MetricSummaryBar toggles
  showApplications: true, // Default to true
  setShowApplications: (show) => set({ showApplications: show }),
  showAttendees: true, // Default to true
  setShowAttendees: (show) => set({ showAttendees: show }),
  showFeederSchools: true, // Default to true
  setShowFeederSchools: (show) => set({ showFeederSchools: show }),
  showUniversities: true, // Default to true
  setShowUniversities: (show) => set({ showUniversities: show }),
  // showEnquiries already exists and is initialized

  // US Funnel stages (Admit, Deposit, Enroll) - map layers
  showOffers: true,  // Admit stage - default true
  setShowOffers: (show) => set({ showOffers: show }),
  showDeposits: true,  // Deposit stage - default true
  setShowDeposits: (show) => set({ showDeposits: show }),
  showEnrollments: true,  // Enroll stage - default true
  setShowEnrollments: (show) => set({ showEnrollments: show }),

  // === Filter Selections ===
  selectedDateRange: null,
  setSelectedDateRange: (range) => set({ selectedDateRange: range }),
  selectedTimeRange: null,
  setSelectedTimeRange: (label) => set({ selectedTimeRange: label }),
  selectedSegment: 'all',
  setSelectedSegment: (segment) => set({ selectedSegment: segment }),
  selectedSchool: 'all',
  setSelectedSchool: (school) => set({ selectedSchool: school }),
  selectedEventType: 'all',
  setSelectedEventType: (eventType) => set({ selectedEventType: eventType }),
  selectedApplicantType: 'all',
  setSelectedApplicantType: (applicantType) => set({ selectedApplicantType: applicantType }),
  selectedApplicantTypes: [],
  setSelectedApplicantTypes: (applicantTypes) => set({ selectedApplicantTypes: applicantTypes }),
  selectedSubject: 'all',
  setSelectedSubject: (subject) => set({ selectedSubject: subject }),
  selectedPostcodePrefix: 'all',
  setSelectedPostcodePrefix: (prefix) => set({ selectedPostcodePrefix: prefix }),
  selectedUniversity: 'all',
  setSelectedUniversity: (university) => set({ selectedUniversity: university }),
  selectedYears: [], // Empty array means all years selected
  setSelectedYears: (years) => set({ selectedYears: years }),

  // === Bottom Panel Dropdown Selections ===
  awarenessSelection: 'postcode-analysis',
  setAwarenessSelection: (selection) => set({ awarenessSelection: selection }),
  considerationSelection: 'subject-interest',
  setConsiderationSelection: (selection) => set({ considerationSelection: selection }),
  conversionSelection: 'enquiry-to-app-by-source',
  setConversionSelection: (selection) => set({ conversionSelection: selection }),

  // === Multi-Selections ===
  selectedSchools: [],
  setSelectedSchools: (schools) => set({ selectedSchools: schools }),
  selectedSubjects: [],
  setSelectedSubjects: (subjects) => set({ selectedSubjects: subjects }),
  selectedPostcodePrefixes: [],
  setSelectedPostcodePrefixes: (postcodes) => set({ selectedPostcodePrefixes: postcodes }),
  selectedEventTypes: [],
  setSelectedEventTypes: (eventTypes) => set({ selectedEventTypes: eventTypes }),
  selectedCounties: [],
  setSelectedCounties: (counties) => set({ selectedCounties: counties }),
  selectedUniversities: [],
  setSelectedUniversities: (universities) => set({ selectedUniversities: universities }),
  topBarSearchTerm: '',
  setTopBarSearchTerm: (term) => set({ topBarSearchTerm: term }),

  // === Logic Operators ===
  logicEnquiryEvent: 'OR',
  setLogicEnquiryEvent: (logic) => set({ logicEnquiryEvent: logic }),
  logicEventApplicant: 'OR',
  setLogicEventApplicant: (logic) => set({ logicEventApplicant: logic }),

  // === Available Filter Options ===
  availableSchools: [],
  setAvailableSchools: (schools) => set({ availableSchools: schools }),
  availableEventTypes: [],
  setAvailableEventTypes: (types) => set({ availableEventTypes: types }),
  availableApplicantTypes: [],
  setAvailableApplicantTypes: (types) => set({ availableApplicantTypes: types }),
  availableSubjects: [],
  setAvailableSubjects: (subjects) => set({ availableSubjects: subjects }),
  availablePostcodePrefixes: [],
  setAvailablePostcodePrefixes: (prefixes) => set({ availablePostcodePrefixes: prefixes }),
  availableYears: [],
  setAvailableYears: (years) => set({ availableYears: years }),
  availableCounties: [],
  setAvailableCounties: (counties) => set({ availableCounties: counties }),
  availableUniversities: [],
  setAvailableUniversities: (universities) => set({ availableUniversities: universities }),

  // === Dynamic Counts ===
  enquiryCount: 0,
  setEnquiryCount: (count) => set({ enquiryCount: count }),
  eventCount: 0,
  setEventCount: (count) => set({ eventCount: count }),
  applicantCount: 0,
  setApplicantCount: (count) => set({ applicantCount: count }),
  enrolmentCount: 0,
  setEnrolmentCount: (count) => set({ enrolmentCount: count }),
  // Initialize counts for MetricSummaryBar
  applicationCount: 0,
  setApplicationCount: (count) => set({ applicationCount: count }),
  offersCount: 0,
  setOffersCount: (count) => set({ offersCount: count }),
  depositsCount: 0,
  setDepositsCount: (count) => set({ depositsCount: count }),
  attendeeCount: 0,
  setAttendeeCount: (count) => set({ attendeeCount: count }),
  feederSchoolCount: 0,
  setFeederSchoolCount: (count) => set({ feederSchoolCount: count }),
  impressionsCount: 0, // Added
  clicksCount: 0, // Added
  setImpressionsCount: (count) => set({ impressionsCount: count }), // Added
  setClicksCount: (count) => set({ clicksCount: count }), // Added
  universitiesCount: 0, // Universities count
  setUniversitiesCount: (count) => set({ universitiesCount: count }), // Universities count setter
  sessionsCount: 0, // GA4 sessions
  setSessionsCount: (count) => set({ sessionsCount: count }),
  showSessions: true, // Show sessions by default
  setShowSessions: (show) => set({ showSessions: show }),

  // Market share demographics
  eighteenYearOldsCount: 0,
  setEighteenYearOldsCount: (count) => set({ eighteenYearOldsCount: count }),
  showEighteenYearOlds: true, // Show demographic by default
  setShowEighteenYearOlds: (show) => set({ showEighteenYearOlds: show }),


  // === Base Layer Toggles ===
  showUkCounties: false, // Default off
  setShowUkCounties: (show) => set({ showUkCounties: show }),
  showUkOoh: false, // Default off
  setShowUkOoh: (show) => set({ showUkOoh: show }),


  // Initialize state for client-specific school layers
  showTargetSchools: true, // Default to true so users can see schools
  setShowTargetSchools: (show) => set({ showTargetSchools: show }),

  showOutreachSchools: true, // Default to true so users can see schools
  setShowOutreachSchools: (show) => set({ showOutreachSchools: show }),
  showUkPcons: false, // Default off - Will be removed/replaced
  setShowUkPcons: (show) => set({ showUkPcons: show }), // Added setter - Will be removed/replaced
  baseLayerVisibility: {}, // Initialize as empty object
  
  setBaseLayerVisibility: (layerId, isVisible) => set((state) => ({
    baseLayerVisibility: {
      ...state.baseLayerVisibility,
      [layerId]: isVisible,
    }
  })),

  // Clear data trigger
  clearDataTrigger: 0,
  triggerClearData: () => set((state) => ({ clearDataTrigger: state.clearDataTrigger + 1 })),

  // === Analytics Layer Toggles & Config ===
  showImpressions: false,
  setShowImpressions: (show) => set({ showImpressions: show }),
  impressionsVizType: 'heatmap', // Default viz
  setImpressionsVizType: (type) => set({ impressionsVizType: type }),
  impressionsColor: '#8B5CF6',  // Purple - matches Impressions funnel slice
  setImpressionsColor: (color) => set({ impressionsColor: color }),
  impressionsOpacity: 50,
  setImpressionsOpacity: (opacity) => set({ impressionsOpacity: opacity }),

  showClicks: false,
  setShowClicks: (show) => set({ showClicks: show }),
  clicksVizType: 'dots', // Default viz
  setClicksVizType: (type) => set({ clicksVizType: type }),
  clicksColor: '#3B82F6',  // Blue - matches Clicks funnel slice
  setClicksColor: (color) => set({ clicksColor: color }),
  clicksOpacity: 100,
  setClicksOpacity: (opacity) => set({ clicksOpacity: opacity }),

  showConversions: false,
  setShowConversions: (show) => set({ showConversions: show }),
  // Add viz/color state for conversions if needed later
  // After showConversions: false, add:
conversionsVizType: 'dots', // Default viz type
setConversionsVizType: (type) => set({ conversionsVizType: type }),
conversionsColor: '#10B981', // Green color by default
setConversionsColor: (color) => set({ conversionsColor: color }),
conversionsOpacity: 100,
setConversionsOpacity: (opacity) => set({ conversionsOpacity: opacity }),
conversionsCount: 0,
setConversionsCount: (count) => set({ conversionsCount: count }),

  // Preset refresh trigger implementation
  sidebarPresetsRefreshCounter: 0,
  triggerSidebarPresetsRefresh: () => set((state) => ({ sidebarPresetsRefreshCounter: state.sidebarPresetsRefreshCounter + 1 })),

  // UI State Management implementation
  activeDropdown: null,
  setActiveDropdown: (dropdownId) => set({ activeDropdown: dropdownId }),

  // Presets implementation - Use Record structure
  presets: {}, // Initial state is an empty object
  addPreset: (preset) => set((state) => {
    const key = preset.preset_id || preset.id; // Use preset_id or id as key
    if (!key) {
      console.error("Preset must have a preset_id or id to be added:", preset);
      return {}; // Return empty object to avoid changing state
    }
    return {
      presets: {
        ...state.presets,
        [key]: preset,
      }
    };
  }),
  deletePreset: (presetId) => set((state) => {
    const { [presetId]: _, ...remainingPresets } = state.presets; // Destructure to remove the key
    return { presets: remainingPresets };
  }),
  loadPresets: (newPresets) => set({ presets: newPresets }), // Directly set the new record

  // Applied Preset Tracking
  appliedPresetId: null,

  // Map bounds for spatial filtering
  currentMapBounds: null,
  setCurrentMapBounds: (bounds) => set((state) => {
    const nextState: Partial<StoreState> = { currentMapBounds: bounds };
    if (state.mapViewFilterEnabled) {
      nextState.spatialFilter = {
        type: 'bounds',
        label: 'In-view bounds',
        bounds: bounds ?? undefined
      };
    }
    return nextState;
  }),
  mapViewFilterEnabled: false,
  setMapViewFilterEnabled: (enabled) => set((state) => {
    let nextSpatial = state.spatialFilter;
    if (enabled) {
      nextSpatial = {
        type: 'bounds',
        label: 'In-view bounds',
        bounds: state.currentMapBounds ?? undefined
      };
    } else if (state.spatialFilter?.type === 'bounds') {
      nextSpatial = null;
    }
    return {
      mapViewFilterEnabled: enabled,
      spatialFilter: nextSpatial
    };
  }),
  spatialFilter: null,
  setSpatialFilter: (filter) => set({ spatialFilter: filter }),

  // Filtered data for postcode analysis
  filteredCRMFeatures: [],
  setFilteredCRMFeatures: (features) => set({ filteredCRMFeatures: features }),
  allCRMFeatures: [],
  setAllCRMFeatures: (features) => set({ allCRMFeatures: features }),
  yearFilteredCRMFeatures: [],
  setYearFilteredCRMFeatures: (features) => set({ yearFilteredCRMFeatures: features }),

  // GA4 Analytics Data
  ga4Overview: null,
  setGA4Overview: (overview) => set({ ga4Overview: overview }),
  ga4TrafficSources: [],
  setGA4TrafficSources: (sources) => set({ ga4TrafficSources: sources }),
  ga4Locations: null,
  setGA4Locations: (locations) => set({ ga4Locations: locations }),
  ga4ContentEngagement: [],
  setGA4ContentEngagement: (content) => set({ ga4ContentEngagement: content }),
  ga4Events: [],
  setGA4Events: (events) => set({ ga4Events: events }),

  // Country selection and highlighting
  selectedCountry: null,
  setSelectedCountry: (country) => set({ selectedCountry: country }),
  countryHighlightVisible: false,
  setCountryHighlightVisible: (visible) => set({ countryHighlightVisible: visible }),
  
  // Country information for clicked countries
  selectedCountryInfo: null,
  setSelectedCountryInfo: (info) => set({ selectedCountryInfo: info }),
  
  // User preferences
  userPreferences: null,
  setUserPreferences: (preferences) => set({ userPreferences: preferences }),
  applyUserPreferences: (preferences) => {
    const newState: Partial<StoreState> = {};
    
    // Apply default metrics visibility
    if (preferences.defaultMetrics) {
      // Reset all metric toggles first
      newState.showApplications = false;
      newState.showAttendees = false;
      newState.showFeederSchools = false;
      newState.showEnquiries = false;
      newState.showEighteenYearOlds = false;
      newState.showImpressions = false;
      newState.showClicks = false;
      newState.showConversions = false;
      
      // Enable selected metrics
      preferences.defaultMetrics.forEach(metric => {
        switch (metric) {
          case 'applications':
            newState.showApplications = true;
            break;
          case 'attendees':
            newState.showAttendees = true;
            break;
          case 'feederSchools':
            newState.showFeederSchools = true;
            break;
          case 'enquiries':
            newState.showEnquiries = true;
            break;
          case 'eighteenYearOlds':
            newState.showEighteenYearOlds = true;
            break;
          case 'impressions':
            newState.showImpressions = true;
            break;
          case 'clicks':
            newState.showClicks = true;
            break;
          case 'conversions':
            newState.showConversions = true;
            break;
        }
      });
    }
    
    // Apply default filters
    if (preferences.defaultFilters) {
      Object.entries(preferences.defaultFilters).forEach(([key, value]) => {
        if (key in newState || key in get()) {
          (newState as any)[key] = value;
        }
      });
    }
    
    // Apply the new state
    set(newState);
    console.log('Applied user preferences:', preferences);
  },
  
  // Comparison mode implementation
  comparisonState: {
    isComparisonMode: false,
    baselineFilters: {
      selectedSchool: 'all',
      selectedEventType: 'all',
      selectedApplicantType: 'all',
      selectedSubject: 'all',
      selectedPostcodePrefix: 'all',
      timeRange: undefined
    },
    comparisonFilters: {
      selectedSchool: 'all',
      selectedEventType: 'all',
      selectedApplicantType: 'all',
      selectedSubject: 'all',
      selectedPostcodePrefix: 'all',
      timeRange: undefined
    },
    baselineData: {
      enquiryCount: 0,
      eventCount: 0,
      applicantCount: 0,
      applicationCount: 0,
      attendeeCount: 0,
      feederSchoolCount: 0,
      impressionsCount: 0,
      clicksCount: 0,
      sessionsCount: 0,
      eighteenYearOldsCount: 0,
      enrolmentCount: 0
    },
    comparisonData: {
      enquiryCount: 0,
      eventCount: 0,
      applicantCount: 0,
      applicationCount: 0,
      attendeeCount: 0,
      feederSchoolCount: 0,
      impressionsCount: 0,
      clicksCount: 0,
      sessionsCount: 0,
      eighteenYearOldsCount: 0,
      enrolmentCount: 0
    }
  },
  
  setComparisonMode: (enabled) => set((state) => ({
    comparisonState: {
      ...state.comparisonState,
      isComparisonMode: enabled
    }
  })),

  // Sidebar dropdown comparison selector. Default 'none'.
  comparisonMode: 'none',
  setComparisonMode2: (mode) => set({ comparisonMode: mode }),

  isComparisonLoading: false,
  setIsComparisonLoading: (loading) => set({ isComparisonLoading: loading }),
  
  setBaselineFilters: (filters) => set((state) => ({
    comparisonState: {
      ...state.comparisonState,
      baselineFilters: {
        ...state.comparisonState.baselineFilters,
        ...filters
      }
    }
  })),
  
  setComparisonFilters: (filters) => set((state) => ({
    comparisonState: {
      ...state.comparisonState,
      comparisonFilters: {
        ...state.comparisonState.comparisonFilters,
        ...filters
      }
    }
  })),
  
  setBaselineData: (data) => set((state) => ({
    comparisonState: {
      ...state.comparisonState,
      baselineData: {
        ...state.comparisonState.baselineData,
        ...data
      }
    }
  })),
  
  setComparisonData: (data) => set((state) => ({
    comparisonState: {
      ...state.comparisonState,
      comparisonData: {
        ...state.comparisonState.comparisonData,
        ...data
      }
    }
  })),
  
  captureCurrentAsBaseline: () => {
    const state = get();
    set({
      comparisonState: {
        ...state.comparisonState,
        baselineFilters: {
          selectedSchool: state.selectedSchool,
          selectedEventType: state.selectedEventType,
          selectedApplicantType: state.selectedApplicantType,
          selectedSubject: state.selectedSubject,
          selectedPostcodePrefix: state.selectedPostcodePrefix,
          timeRange: state.userPreferences?.defaultFilters?.timeRange
        },
      baselineData: {
        enquiryCount: state.enquiryCount,
        eventCount: state.eventCount,
        applicantCount: state.applicantCount,
        applicationCount: state.applicationCount,
        attendeeCount: state.attendeeCount,
        feederSchoolCount: state.feederSchoolCount,
        impressionsCount: state.impressionsCount,
        clicksCount: state.clicksCount,
        sessionsCount: state.sessionsCount,
        eighteenYearOldsCount: state.eighteenYearOldsCount,
        enrolmentCount: state.enrolmentCount
      }
    }
  });
},
  
  captureCurrentAsComparison: () => {
    const state = get();
    set({
      comparisonState: {
        ...state.comparisonState,
        comparisonFilters: {
          selectedSchool: state.selectedSchool,
          selectedEventType: state.selectedEventType,
          selectedApplicantType: state.selectedApplicantType,
          selectedSubject: state.selectedSubject,
          selectedPostcodePrefix: state.selectedPostcodePrefix,
          timeRange: state.userPreferences?.defaultFilters?.timeRange
        },
      comparisonData: {
        enquiryCount: state.enquiryCount,
        eventCount: state.eventCount,
        applicantCount: state.applicantCount,
        applicationCount: state.applicationCount,
        attendeeCount: state.attendeeCount,
        feederSchoolCount: state.feederSchoolCount,
        impressionsCount: state.impressionsCount,
        clicksCount: state.clicksCount,
        sessionsCount: state.sessionsCount,
        eighteenYearOldsCount: state.eighteenYearOldsCount,
        enrolmentCount: state.enrolmentCount
      }
    }
  });
},
  
  // Spatial Analysis - Isochrones
  activeIsochrones: [],
  setActiveIsochrones: (isochrones) => set({ activeIsochrones: isochrones }),
  addIsochrone: (isochrone) => set((state) => ({
    activeIsochrones: [...state.activeIsochrones, isochrone]
  })),
  clearIsochrones: () => set({ activeIsochrones: [] }),
  isGeneratingIsochrone: false,
  setIsGeneratingIsochrone: (generating) => set({ isGeneratingIsochrone: generating }),

  // Target schools list for reporting
  targetSchoolsList: [],
  setTargetSchoolsList: (schools) => set({ targetSchoolsList: schools }),

  // Outreach schools list for reporting
  outreachSchoolsList: [],
  setOutreachSchoolsList: (schools) => set({ outreachSchoolsList: schools }),

  applyPreset: (presetId) => {
    const state = get();
    const preset = state.presets[presetId];

    if (!preset) {
      console.error(`Preset with ID ${presetId} not found.`);
      set({ appliedPresetId: null }); // Clear applied preset if not found
      return;
    }

    // Reset relevant states before applying preset settings
    const newState: Partial<StoreState> = {
      appliedPresetId: presetId,
      showEnquiries: false,
      showEvents: false,
      showApplicant: false,
      // Reset other filters if presets are meant to control them (e.g., selectedSchool: 'all')
      // For now, only resetting layer visibility and setting viz type based on preset.metrics
    };

    // Apply settings from the preset's metrics array
    preset.metrics?.forEach(metricItem => {
      const vizType = metricItem.visualizationType;
      switch (metricItem.metric) {
        case 'enquiries': // Assuming 'enquiries' is a valid MetricValue for the enquiry layer
          newState.showEnquiries = true;
          newState.enquiriesVizType = isValidVisualizationType(vizType) ? vizType : 'dots'; // Validate and default
          // Potentially set color too: newState.enquiriesColor = preset.color || defaultColor;
          break;
        case 'events': // Assuming 'events' is a valid MetricValue for the event layer
          newState.showEvents = true;
          newState.eventsVizType = isValidVisualizationType(vizType) ? vizType : 'heatmap'; // Validate and default
          break;
        case 'applicant_status': // Assuming 'applicant_status' is a valid MetricValue for the applicant layer
          newState.showApplicant = true;
          newState.applicantVizType = isValidVisualizationType(vizType) ? vizType : 'clusters'; // Validate and default
          break;
        // Add cases for other metrics controlled by presets if necessary
        // e.g., case 'school_filter': newState.selectedSchool = preset.schoolFilterValue;
      }
    });

    // Restore county selections from preset
    if (preset.selectedCounties && preset.selectedCounties.length > 0) {
      newState.selectedCounties = preset.selectedCounties;
      newState.showUkCounties = true; // Turn on UK counties layer when counties are selected
    } else {
      newState.selectedCounties = [];
      newState.showUkCounties = false;
    }

    // Restore bottom panel dropdown selections from preset
    if (preset.awarenessDropdown) {
      newState.awarenessSelection = preset.awarenessDropdown;
    }
    if (preset.considerationDropdown) {
      newState.considerationSelection = preset.considerationDropdown;
    }
    if (preset.conversionDropdown) {
      newState.conversionSelection = preset.conversionDropdown;
    }

    // Restore School View state from the preset. All four fields are optional —
    // older presets created before this change won't have them, and in that case
    // we leave the current store value alone (treat absence as no-op, not clear).
    if (preset.selectedSchools !== undefined) {
      newState.selectedSchools = preset.selectedSchools;
    }
    if (preset.spatialFilter !== undefined) {
      newState.spatialFilter = preset.spatialFilter;
    }
    if (preset.selectedDateRange !== undefined) {
      // ISO strings -> Date objects. A null saved range clears the filter.
      if (preset.selectedDateRange === null) {
        newState.selectedDateRange = null;
      } else {
        const from = preset.selectedDateRange.from
          ? new Date(preset.selectedDateRange.from)
          : undefined;
        const to = preset.selectedDateRange.to
          ? new Date(preset.selectedDateRange.to)
          : undefined;
        // react-day-picker's DateRange requires `from` to exist; if neither bound
        // round-trips, fall back to null rather than producing an invalid range.
        newState.selectedDateRange = from ? { from, to } : null;
      }
    }
    if (preset.comparisonMode !== undefined) {
      newState.comparisonMode = preset.comparisonMode;
    }

    set(newState);
    console.log(`Applied preset: ${preset.name} (ID: ${presetId})`); // Changed preset.label to preset.name
    // Optionally trigger other actions needed after applying a preset
  },
}));

// Initialization logic needs to be updated elsewhere (e.g., in _app.tsx or AuthProvider)
// to fetch the client object based on the stored ID after the app loads.
// We cannot directly set the client object here as it requires an async fetch.
